import React from 'react';
import Icons from './Icons.tsx';
import {Box, Typography, IconButton, Chip, Menu, MenuItem, Divider} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {Popup} from 'react-leaflet';
import {usePositionEditing} from './PositionEditingProvider.tsx';
import type { Position, TagCategory, Tag } from '../types/position.ts';
import { useTagsRegistry } from './TagsProvider.tsx';
import { assignPositionTag, unassignPositionTag } from '../utils/tagsApi.ts';
import { buildTagTree, getTagInCategory } from '../utils/tags.ts';

const PopupRow = ({ icon, label, value }: { icon: JSX.Element; label: string; value: string | number | JSX.Element | null }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
    {icon}
    <Typography variant="caption" sx={{ fontWeight: 500, minWidth: 100, mr: "0.5rem" }}>{label}</Typography>
    <Typography variant="caption">{value ?? '-'}</Typography>
  </Box>
);

interface PositionPopupProps {
  position: Position;
  onClose: () => void;
  openDrawerEditing: () => void;
  pinDrawerEditing: () => void;
  viewportHeight?: number;
  viewportWidth?: number;
}

// -----------------------------------------------------------------------------
// One chip per effective tag category; clicking opens a tree-picker menu.
// -----------------------------------------------------------------------------
const TagChipSection: React.FC<{ position: Position }> = ({ position }) => {
  const { registry, refresh } = useTagsRegistry();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [activeCat, setActiveCat] = React.useState<TagCategory | null>(null);

  if (registry.effectiveCategories.length === 0) return null;

  const handleOpen = (e: React.MouseEvent<HTMLElement>, cat: TagCategory) => {
    setAnchorEl(e.currentTarget);
    setActiveCat(cat);
  };
  const handleClose = () => { setAnchorEl(null); setActiveCat(null); };

  const handlePick = async (tagId: number | null) => {
    if (!activeCat) return;
    try {
      if (tagId == null) {
        const current = getTagInCategory(position, activeCat.id, registry);
        if (current) await unassignPositionTag(position.id, current.id);
      } else {
        await assignPositionTag(position.id, tagId);
      }
      await refresh();
      if (!position.tag_ids) position.tag_ids = [];
      const idx = position.tag_ids.findIndex(id => registry.tags.get(id)?.category_id === activeCat.id);
      if (idx >= 0) position.tag_ids.splice(idx, 1);
      if (tagId != null) position.tag_ids.push(tagId);
    } catch (err) {
      console.error('tag assignment failed', err);
    } finally {
      handleClose();
    }
  };

  const catTags = activeCat
    ? Array.from(registry.tags.values()).filter(t => t.category_id === activeCat.id)
    : [];
  const catTree = buildTagTree(catTags);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>Tags</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {registry.effectiveCategories.map(c => {
          const assigned = getTagInCategory(position, c.id, registry);
          return (
            <Chip
              key={c.id}
              size="small"
              icon={<LocalOfferIcon fontSize="small" />}
              label={`${c.tag_category}: ${assigned?.tag ?? '—'}`}
              onClick={(e) => handleOpen(e, c)}
              variant={assigned ? 'filled' : 'outlined'}
            />
          );
        })}
      </Box>
      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItem onClick={() => handlePick(null)}>
          <em>Clear</em>
        </MenuItem>
        <Divider />
        <TagMenuTree tree={catTree} depth={0} onPick={handlePick} />
      </Menu>
    </>
  );
};

const TagMenuTree: React.FC<{
  tree: ReturnType<typeof buildTagTree>;
  depth: number;
  onPick: (tagId: number) => void;
}> = ({ tree, depth, onPick }) => (
  <>
    {tree.map(node => (
      <React.Fragment key={node.tag.id}>
        <MenuItem onClick={() => onPick(node.tag.id)} sx={{ pl: 2 + depth * 2 }}>
          {node.tag.tag}
        </MenuItem>
        {node.children.length > 0 && (
          <TagMenuTree tree={node.children} depth={depth + 1} onPick={onPick} />
        )}
      </React.Fragment>
    ))}
  </>
);

const PositionPopup: React.FC<PositionPopupProps> = ({
  position,
  onClose,
  openDrawerEditing,
  pinDrawerEditing,
  viewportHeight = window.innerHeight,
  viewportWidth = window.innerWidth
}) => {
  const editing = usePositionEditing();

  // Calculate responsive popup dimensions: 70% of viewport with reasonable max/min
  const maxHeight = Math.min(Math.max(viewportHeight * 0.7, 400), 900);
  const maxWidth = Math.min(Math.max(viewportWidth * 0.7, 300), 650);
  const contentHeight = Math.max(maxHeight - 60, 350);
  const contentWidth = Math.max(maxWidth - 20, 280);

  return (
    <>
      <Popup
        className="position-popup"
        position={[position.latitude, position.longitude]}
        closeButton={false}
        eventHandlers={{
          remove: onClose,
        }}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
      >
        <Box sx={{ maxHeight: contentHeight, overflowY: 'auto', maxWidth: contentWidth }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {Icons.popup.heading.point}
              <Typography variant="subtitle1" fontWeight={600}>
                Position #{position.count}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => {
                  openDrawerEditing();
                  pinDrawerEditing();
                  editing.setFromPositionPopup(position.id);
                }}
                aria-label="Edit position"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close popup"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ fontSize: 12, lineHeight: 1.4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Identifiers
              </Typography>
            </Box>
            <PopupRow icon={Icons.popup.content.user} label="User Id" value={position.user_id} />
            <PopupRow icon={Icons.popup.content.track} label="Track Id" value={position.track_id} />
            <PopupRow icon={Icons.popup.content.track} label="Position Id" value={position.id} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Raw Attributes
              </Typography>
            </Box>
            <PopupRow icon={Icons.popup.content.date} label="Date" value={position.time} />
            <PopupRow icon={Icons.popup.content.time} label="Time" value={position.time} />
            <PopupRow icon={Icons.popup.content.latitude} label="Coordinates" value={position.latitude.toFixed(4) + ', ' + position.longitude.toFixed(4)} />
            <PopupRow icon={Icons.popup.content.altitude} label="Altitude" value={`${position.altitude?.toFixed(2) ?? '-'} m`} />
            <PopupRow icon={Icons.popup.content.speed} label="Speed" value={`${position.speed?.toFixed(2) ?? '-'} m/s`} />
            <PopupRow icon={Icons.popup.content.bearing} label="Bearing" value={`${position.bearing?.toFixed(2) ?? '-'}°`} />
            <PopupRow icon={Icons.popup.content.accuracy} label="Accuracy" value={`${position.accuracy?.toFixed(2) ?? '-'} m`} />
            <PopupRow icon={Icons.popup.content.provider} label="Provider" value={position.provider ?? '—'} />
            <PopupRow icon={Icons.popup.content.travel_mode} label="Travel Mode" value={position.travel_mode ?? '—'} />
            <PopupRow icon={Icons.popup.content.comment} label="Comment" value={position.comment ?? '—'} />
            <PopupRow icon={Icons.popup.content.image} label="Image" value={position.image ?? '—'} />
            <PopupRow icon={Icons.popup.content.source} label="Source" value={position.source ?? '—'} />
            <PopupRow icon={Icons.popup.content.additional_data} label="Extra" value={
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {position.additional_data === null ? '—' : JSON.stringify(position.additional_data, null, 2)}
              </pre>
            } />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Calculated Attributes
              </Typography>
            </Box>
            <PopupRow icon={Icons.popup.content.distance} label="Distance" value={`${position.distance?.toFixed(2) ?? '-'} m`} />
            <PopupRow icon={Icons.popup.content.duration} label="Duration" value={`${position.duration?.toFixed(2) ?? '-'} s`} />
            <PopupRow icon={Icons.popup.content.speed} label="Velocity" value={`${position.velocity?.toFixed(2) ?? '-'} m/s`} />
            <PopupRow icon={Icons.popup.content.speed} label="Acceleration" value={`${position.acceleration?.toFixed(2) ?? '-'} m/s²`} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Aggregations
              </Typography>
            </Box>
            <PopupRow icon={Icons.popup.content.distance} label="Total Distance" value={`${position.accumDistance?.toFixed(2) ?? '-'} km`} />
            <PopupRow icon={Icons.popup.content.duration} label="Total Duration" value={`${position.accumDuration?.toFixed(2) ?? '-'} h`} />
            <PopupRow icon={Icons.popup.content.altitude} label="Average Altitude" value={`${position.avgAltitude?.toFixed(2) ?? '-'} m`} />
            <PopupRow icon={Icons.popup.content.speed} label="Average Speed" value={`${position.avgSpeed?.toFixed(2) ?? '-'} m/s`} />

            <TagChipSection position={position} />
          </Box>

        </Box>
      </Popup>
    </>
  );
};

export default PositionPopup;
